import React, { useImperativeHandle, forwardRef, useCallback, useState } from 'react';
import { Modal, Button } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import toastr from 'toastr';
import { useDispatch } from 'react-redux';
import * as TaskUtilities from '../../HelperMethods/TaskUtilities';
import { parseJSONStringToObject } from '../../HelperMethods/UtilityFunctions';
import { bulkDeleteTasks, bulkUnAssignTasks } from './TaskViewPage/redux/actions';

const DeleteTasksModal = forwardRef((props, ref) => {
  const { taskGridApi, handleClearSelectedTasks } = props;
  const dispatch = useDispatch();
  const [deleteModal, handleDeleteModal] = useState({
    content: 'Do you want to delete this task?',
    deleteChildTasksContent: '',
    header: 'Warning',
    open: false,
    onConfirm: () => {},
    onCancel: () => {},
    onDeleteParentChildGroup: () => {},
  });
  const [childTasksDeleteButton, showChildTasksDeleteButton] = useState(false);

  const handleCloseModal = useCallback(() => {
    handleDeleteModal((prevState) => ({ ...prevState, deleteChildTasksContent: '', open: false }));
    showChildTasksDeleteButton(false);
  }, []);

  const handleBulkDelete = useCallback(
    async (selectedTasks) => {
      try {
        await dispatch(bulkDeleteTasks(selectedTasks));
        if (taskGridApi) {
          taskGridApi.deselectAll();
        }
        if (handleClearSelectedTasks) {
          handleClearSelectedTasks();
        }
        toastr.success('Tasks Deleted Successfully!!');
      } catch (error) {
        toastr.error('Error in task delete');
      } finally {
        handleCloseModal();
      }
    },
    [dispatch, handleClearSelectedTasks, handleCloseModal, taskGridApi]
  );

  const handleBulkUnassign = useCallback(
    async (selectedTasks) => {
      try {
        await dispatch(bulkUnAssignTasks(selectedTasks));
        toastr.success('Tasks Unassigned Successfully!!');
        if (taskGridApi) {
          taskGridApi.deselectAll();
        }
        if (handleClearSelectedTasks) {
          handleClearSelectedTasks();
        }
      } catch (error) {
        toastr.error('Task Unassigned Failed!!');
      } finally {
        handleCloseModal();
      }
    },
    [dispatch, handleClearSelectedTasks, handleCloseModal, taskGridApi]
  );

  const handleBulkDeleteAllChildTasks = useCallback(
    async (selectedTasks) => {
      const { message, output } = await dispatch(bulkDeleteTasks([], true, true, selectedTasks));
      const parsedOutput = parseJSONStringToObject(output) || {};
      showChildTasksDeleteButton(false);
      const tasks = parsedOutput.allTasksToDeleted;
      handleDeleteModal((prevState) => ({
        ...prevState,
        deleteChildTasksContent: message,
        onConfirm: async () => {
          await dispatch(bulkDeleteTasks(tasks));
          toastr.success('Tasks deleted successfully');
          handleDeleteModal((prev) => ({ ...prev, deleteChildTasksContent: '', open: false }));
        },
        onCancel: () => {
          showChildTasksDeleteButton(true);
          handleDeleteModal((prev) => ({
            ...prev,
            deleteChildTasksContent: '',
            open: true,
            onConfirm: () => handleBulkDelete(selectedTasks),
            onCancel: handleCloseModal,
            onDeleteParentChildGroup: () => handleBulkDeleteAllChildTasks(selectedTasks),
          }));
        },
      }));
    },
    [dispatch, handleBulkDelete, handleCloseModal]
  );

  const handleOpenModal = useCallback(() => {
    handleDeleteModal((prevState) => ({ ...prevState, open: true }));
  }, []);

  useImperativeHandle(ref, () => ({
    async handleDeleteTasks(taskList) {
      const childTasks = taskList.filter((e) => TaskUtilities.isTaskChild(e));
      let content = taskList?.length > 1 ? 'Do you want to delete these tasks?' : 'Do you want to delete this task?';
      let tasks = [];
      if (childTasks?.length) {
        showChildTasksDeleteButton(true);
        const tasksData = taskList.map(({ parentTaskId, id }) => {
          return {
            parentTaskId,
            id,
          };
        });
        const { message, output } = await dispatch(bulkDeleteTasks(tasksData, true, true));
        const parsedOutput = parseJSONStringToObject(output) || {};
        const { validation = true } = parsedOutput;
        if (!validation) {
          content = message;
        }
        tasks = parsedOutput.allTasksToDeleted;
      } else {
        tasks = taskList.map((val) => {
          return { id: val.id };
        });
      }
      handleDeleteModal((prevState) => ({
        ...prevState,
        open: true,
        content,
        onConfirm: () => handleBulkDelete(tasks),
        onCancel: handleCloseModal,
        onDeleteParentChildGroup: () => handleBulkDeleteAllChildTasks(tasks),
      }));
    },

    async handleUnassignTasks(tasks) {
      handleDeleteModal((prevState) => ({
        ...prevState,
        open: true,
        header: 'Warning',
        content: 'Do you want to un-assign these tasks?',
        onConfirm: () => handleBulkUnassign(tasks),
        onCancel: handleCloseModal,
      }));
    },
  }));

  return (
    <Modal onClose={handleCloseModal} onOpen={handleOpenModal} open={deleteModal?.open} size="small">
      <Modal.Header>{deleteModal.header}</Modal.Header>
      <Modal.Content>
        <Modal.Description>{deleteModal?.deleteChildTasksContent || deleteModal?.content}</Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button content="Cancel" onClick={deleteModal?.onCancel} style={{ margin: '0 10px 0 0' }} />

        <>
          <Button primary content="OK" onClick={deleteModal?.onConfirm} style={{ margin: '0 10px 0 0' }} />
          {childTasksDeleteButton && (
            <Button
              primary
              content="Delete Parent-Child Group"
              onClick={deleteModal?.onDeleteParentChildGroup}
              style={{ margin: '0 10px 0 0' }}
            />
          )}
        </>
      </Modal.Actions>
    </Modal>
  );
});

DeleteTasksModal.propTypes = {
  taskGridApi: PropTypes.any,
  handleClearSelectedTasks: PropTypes.func,
};

export default DeleteTasksModal;
